/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { ImgHTMLAttributes, ReactNode } from 'react'
import { SerializedStyles } from '@emotion/react'
import {
  brandLogo,
  brandRow,
  headline,
  leftCol,
  rightCol,
  row,
  spacer,
  wrapper,
} from '../../Guide/ui/GuideOverviewBanner.style'
import { root } from '../../Guide/ui/GuideSearch.style'

interface BannerProps {
  children: ReactNode
  className?: string
  testId?: string
}
interface BannerImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'css'> {
  src: string
  alt: string
  css?: SerializedStyles
}

interface BannerHeadlineProps {
  children: ReactNode
  css?: SerializedStyles
}

interface BannerLogoProps {
  src: string
  alt: string
  testId?: string
  css?: SerializedStyles
}

const SplashBanner = ({ children, className, testId }: BannerProps) => {
  return (
    <Box css={root} className={className} data-testid={testId}>
      <Box css={row}>{children}</Box>
      <Box css={spacer} />
    </Box>
  )
}

const Wrapper = ({ children, ...props }: BannerProps) => {
  const theme = useTheme()
  return (
    <Box css={wrapper(theme)} {...props}>
      {children}
    </Box>
  )
}

const LeftColumn = ({ children, testId, ...props }: BannerProps) => {
  const theme = useTheme()
  return (
    <Box css={leftCol(theme)} data-testid={testId} {...props}>
      {children}
    </Box>
  )
}

const RightColumn = ({ children, ...props }: BannerProps) => {
  const theme = useTheme()
  return (
    <Box css={rightCol(theme)} {...props}>
      {children}
    </Box>
  )
}

const Logo = ({ src, alt, testId, css: customCss, ...props }: BannerLogoProps) => {
  const theme = useTheme()
  return (
    <Box css={brandRow(theme)} data-test-id={testId}>
      <Box component="img" src={src} alt={alt} css={[brandLogo(theme), customCss]} {...props} />
    </Box>
  )
}

const Headline = ({ children, css: customCss }: BannerHeadlineProps) => {
  const theme = useTheme()
  return <h1 css={[headline(theme), customCss]}>{children}</h1>
}

const Image = ({ src, alt, css: customCss, ...props }: BannerImageProps) => {
  return (
    <Box component="img" src={src} alt={alt} sx={{ maxHeight: 400 }} css={customCss} {...props} />
  )
}

SplashBanner.Wrapper = Wrapper
SplashBanner.LeftColumn = LeftColumn
SplashBanner.RightColumn = RightColumn
SplashBanner.Logo = Logo
SplashBanner.Headline = Headline
SplashBanner.Image = Image

export { SplashBanner }
